import React, { useEffect, useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { getOfferCatalog, getCampaignOffer, setCampaignOffer } from '../utils/api';

export const OfferSelector = ({ campaignId, recommendedOffers = [], onClose, onSelected }) => {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null);
  const [customText, setCustomText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getOfferCatalog(), getCampaignOffer(campaignId)])
      .then(([cat, cur]) => {
        setCatalog(cat.data);
        setSelected(cur.data.selected_offer);
        setCustomText(cur.data.custom_offer_text || '');
      })
      .catch((e) => console.error('offer load failed', e));
  }, [campaignId]);

  const save = async (offerId, textOverride) => {
    setSaving(true);
    try {
      const text = textOverride !== undefined ? textOverride : customText;
      await setCampaignOffer(campaignId, offerId, text);
      setSelected(offerId);
      if (textOverride !== undefined) setCustomText(textOverride);
      onSelected?.(offerId);
    } catch (e) {
      console.error('offer save failed', e);
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) return null;
  const ordered = [
    ...recommendedOffers.filter((id) => catalog[id]),
    ...Object.keys(catalog).filter((id) => !recommendedOffers.includes(id)),
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="offer-selector-modal">
      <div className="bg-card-bg border border-divider rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-divider flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Choose Your Offer</h2>
            <p className="text-sm text-gray mt-1">Your offer feeds into every personalized asset, ad, email and script for this campaign.</p>
          </div>
          <button onClick={onClose} data-testid="offer-close-btn" className="text-gray hover:text-white p-2"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ordered.map((id) => {
              const o = catalog[id];
              const isRec = recommendedOffers.includes(id);
              const isSel = selected === id;
              return (
                <button
                  key={id}
                  onClick={() => save(id, '')}
                  disabled={saving}
                  data-testid={`offer-card-${id}`}
                  className={`text-left p-4 rounded-lg border-2 transition-colors ${
                    isSel ? 'border-lime bg-lime/5' : 'border-divider bg-black hover:border-lime/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5 gap-2">
                    <p className="text-white font-bold">{o.label}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      {isRec && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange/15 text-orange border border-orange/40">RECOMMENDED</span>}
                      {isSel && <Check size={16} className="text-lime" data-testid="offer-selected-check" />}
                    </div>
                  </div>
                  <p className="text-xs text-lime mb-2 italic">e.g. “{o.example}”</p>
                  <div className="space-y-1 text-xs text-light-gray">
                    <p><span className="text-muted">Best for:</span> {o.best_for}</p>
                    <p><span className="text-muted">Why it works:</span> {o.why_it_works}</p>
                    <p><span className="text-muted">Margin risk:</span> {o.margin_risk}</p>
                    <p><span className="text-muted">Urgency:</span> {o.urgency_level}</p>
                    <p><span className="text-muted">Suggested CTA:</span> “{o.suggested_cta}”</p>
                    {o.compliance_warning && (
                      <p className="text-orange flex items-start gap-1 pt-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {o.compliance_warning}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className={`mt-5 p-4 rounded-lg bg-black border-2 transition-colors ${selected === 'custom' ? 'border-lime' : 'border-divider'}`} data-testid="offer-custom-section">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm text-white font-bold">Write Your Own Offer</label>
              {selected === 'custom' && <Check size={16} className="text-lime" data-testid="offer-custom-selected-check" />}
            </div>
            <p className="text-xs text-gray mb-3">Type exactly what you want to offer — your price, your terms. It feeds your AI personalization, assets, and tracking the same way a pre-written offer does.</p>
            <div className="flex gap-3">
              <input
                data-testid="offer-custom-input"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder='e.g. "$79 tune-up + free filter this month" or "Free second opinion on any repair quote over $500"'
                className="flex-1 bg-card-bg border border-divider rounded-lg px-4 py-2.5 text-white text-sm focus:border-lime focus:outline-none"
              />
              <button
                onClick={() => save('custom')}
                disabled={!customText.trim() || saving}
                data-testid="offer-save-custom-btn"
                className="px-5 py-2.5 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Use This Offer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
